import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

/**
 * Text-to-speech hook built on the browser's SpeechSynthesis API.
 *
 * Tracks which utterance id is currently speaking so the UI can flip the
 * play/stop button on just the right message. Only one utterance speaks
 * at a time - starting a new one cancels the previous.
 */
export interface UseSpeechResult {
  supported: boolean
  speakingId: string | null
  speak: (id: string, text: string) => void
  stop: () => void
  preferredVoice: SpeechSynthesisVoice | null
}

function stripForSpeech(src: string): string {
  if (!src) return ''
  // Drop fenced code blocks and stray HTML; collapse markdown markers
  // so the narration doesn't read "asterisk asterisk hello asterisk asterisk".
  return src
    .replace(/```[\s\S]*?```/g, ' code block omitted. ')
    .replace(/<[^>]+>/g, ' ')
    .replace(/\*\*([^*]+)\*\*/g, '$1')
    .replace(/\*([^*]+)\*/g, '$1')
    .replace(/`([^`]+)`/g, '$1')
    .replace(/!?\[([^\]]+)\]\(([^)]+)\)/g, '$1')
    .replace(/^#+\s+/gm, '')
    .replace(/\s+/g, ' ')
    .trim()
}

export function useSpeech(): UseSpeechResult {
  const supported = useMemo(
    () => typeof window !== 'undefined' && 'speechSynthesis' in window,
    [],
  )
  const [speakingId, setSpeakingId] = useState<string | null>(null)
  const [voices, setVoices] = useState<SpeechSynthesisVoice[]>([])
  const utteranceRef = useRef<SpeechSynthesisUtterance | null>(null)

  useEffect(() => {
    if (!supported) return
    const load = () => {
      setVoices(window.speechSynthesis.getVoices())
    }
    load()
    window.speechSynthesis.onvoiceschanged = load
    return () => {
      try { window.speechSynthesis.cancel() } catch { /* ignore */ }
    }
  }, [supported])

  const preferredVoice = useMemo(() => {
    if (voices.length === 0) return null
    // Prefer a natural-sounding English voice when available.
    const prefs = [
      (v: SpeechSynthesisVoice) => v.lang.startsWith('en') && /natural|neural|premium|google|samantha|daniel/i.test(v.name),
      (v: SpeechSynthesisVoice) => v.lang.startsWith('en') && v.default,
      (v: SpeechSynthesisVoice) => v.lang.startsWith('en'),
    ]
    for (const p of prefs) {
      const match = voices.find(p)
      if (match) return match
    }
    return voices[0] ?? null
  }, [voices])

  const speak = useCallback(
    (id: string, text: string) => {
      if (!supported) return
      const clean = stripForSpeech(text)
      if (!clean) return
      try { window.speechSynthesis.cancel() } catch { /* ignore */ }
      const u = new SpeechSynthesisUtterance(clean)
      if (preferredVoice) u.voice = preferredVoice
      u.rate = 1
      u.pitch = 1
      u.volume = 1
      u.onend = () => {
        setSpeakingId((cur) => (cur === id ? null : cur))
      }
      u.onerror = () => {
        setSpeakingId((cur) => (cur === id ? null : cur))
      }
      utteranceRef.current = u
      setSpeakingId(id)
      window.speechSynthesis.speak(u)
    },
    [preferredVoice, supported],
  )

  const stop = useCallback(() => {
    if (!supported) return
    try { window.speechSynthesis.cancel() } catch { /* ignore */ }
    setSpeakingId(null)
  }, [supported])

  return { supported, speakingId, speak, stop, preferredVoice }
}
